import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Wand2,
  SlidersHorizontal,
  Eye,
  Check,
  Minus,
  Rocket,
  Palette,
  GraduationCap,
  ArrowRight,
  ArrowUpRight,
} from 'lucide-react';
import { Canvas, useFrame } from '@react-three/fiber';
import { cn } from '@/lib/utils';
import * as THREE from 'three';
import { motion, useInView } from 'framer-motion';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Logo } from '@/components/Logo';

/* ------------------------------------------------------------------ */
/*  3D Hero scene — wireframe in indigo + teal                         */
/* ------------------------------------------------------------------ */

function RotatingWireframe() {
  const meshRef = useRef<THREE.Mesh>(null);
  const edgesRef = useRef<THREE.LineSegments>(null);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (meshRef.current) {
      meshRef.current.rotation.x = t * 0.12;
      meshRef.current.rotation.y = t * 0.18;
    }
    if (edgesRef.current) {
      edgesRef.current.rotation.x = t * 0.12;
      edgesRef.current.rotation.y = t * 0.18;
    }
  });

  const geometry = new THREE.IcosahedronGeometry(2.5, 1);
  const edges = new THREE.EdgesGeometry(geometry);

  return (
    <group>
      <lineSegments ref={edgesRef} geometry={edges}>
        <lineBasicMaterial color="#170C79" transparent opacity={0.35} />
      </lineSegments>
      <mesh ref={meshRef} geometry={geometry}>
        <meshBasicMaterial color="#56B6C6" transparent opacity={0.05} />
      </mesh>
    </group>
  );
}

function FloatingParticles() {
  const pointsRef = useRef<THREE.Points>(null);
  const count = 80;
  const positions = useRef(new Float32Array(count * 3));

  useEffect(() => {
    for (let i = 0; i < count; i++) {
      positions.current[i * 3] = (Math.random() - 0.5) * 12;
      positions.current[i * 3 + 1] = (Math.random() - 0.5) * 12;
      positions.current[i * 3 + 2] = (Math.random() - 0.5) * 12;
    }
  }, []);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y = state.clock.getElapsedTime() * 0.02;
      pointsRef.current.rotation.x = state.clock.getElapsedTime() * 0.008;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions.current, 3]}
          count={count}
        />
      </bufferGeometry>
      <pointsMaterial color="#170C79" size={0.04} transparent opacity={0.45} sizeAttenuation />
    </points>
  );
}

function HeroScene() {
  return (
    <Canvas
      camera={{ position: [0, 0, 7], fov: 45 }}
      className="pointer-events-none absolute inset-0"
      style={{ background: 'transparent' }}
      gl={{ alpha: true, antialias: true }}
    >
      <ambientLight intensity={0.4} />
      <RotatingWireframe />
      <FloatingParticles />
    </Canvas>
  );
}

/* ------------------------------------------------------------------ */
/*  Scroll-reveal                                                      */
/* ------------------------------------------------------------------ */

function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Animated counter                                                   */
/* ------------------------------------------------------------------ */

function AnimatedStat({
  value,
  suffix,
  label,
}: {
  value: number;
  suffix: string;
  label: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const end = value;
    const duration = 1500;
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress >= 1) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, value]);

  return (
    <div ref={ref} className="text-center">
      <div className="font-display text-5xl font-extrabold tracking-tight text-brand-cream sm:text-6xl">
        {count}
        <span className="text-[0.6em]">{suffix}</span>
      </div>
      <div className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-brand-cream/70 sm:text-sm">
        {label}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function LandingPage() {
  const { t } = useTranslation('landing');
  const [scrolled, setScrolled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const t = requestAnimationFrame(() => setIsLoaded(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const features = [
    {
      icon: Wand2,
      titleKey: 'features.textTo2d3d.title',
      catchKey: 'features.textTo2d3d.tag',
      descKey: 'features.textTo2d3d.desc',
      span: 'lg:col-span-2',
    },
    {
      icon: SlidersHorizontal,
      titleKey: 'features.realtime.title',
      catchKey: 'features.realtime.tag',
      descKey: 'features.realtime.desc',
      span: '',
    },
    {
      icon: Eye,
      titleKey: 'features.multiAngle.title',
      catchKey: 'features.multiAngle.tag',
      descKey: 'features.multiAngle.desc',
      span: '',
    },
    {
      icon: ArrowUpRight,
      titleKey: 'features.export.title',
      catchKey: 'features.export.tag',
      descKey: 'features.export.desc',
      span: 'lg:col-span-2',
    },
  ];

  const steps = [
    {
      number: '01',
      titleKey: 'steps.describe.title',
      descKey: 'steps.describe.desc',
    },
    {
      number: '02',
      titleKey: 'steps.generate.title',
      descKey: 'steps.generate.desc',
    },
    {
      number: '03',
      titleKey: 'steps.editExport.title',
      descKey: 'steps.editExport.desc',
    },
  ];

  const useCases = [
    {
      icon: Rocket,
      titleKey: 'useCases.prototype.title',
      audienceKey: 'useCases.prototype.audience',
      descKey: 'useCases.prototype.desc',
    },
    {
      icon: Palette,
      titleKey: 'useCases.nonExpert.title',
      audienceKey: 'useCases.nonExpert.audience',
      descKey: 'useCases.nonExpert.desc',
    },
    {
      icon: GraduationCap,
      titleKey: 'useCases.education.title',
      audienceKey: 'useCases.education.audience',
      descKey: 'useCases.education.desc',
    },
  ];

  const planFeatures: Array<{
    labelKey: string;
    starter: string | boolean;
    pro: string | boolean;
    business: string | boolean;
  }> = [
    {
      labelKey: 'generations',
      starter: t('planFeatureValues.starterGenerations'),
      pro: t('planFeatureValues.proGenerations'),
      business: t('planFeatureValues.businessGenerations'),
    },
    { labelKey: 'gen2d3d', starter: true, pro: true, business: true },
    { labelKey: 'parametricEdit', starter: true, pro: true, business: true },
    { labelKey: 'export', starter: true, pro: true, business: true },
    {
      labelKey: 'history',
      starter: t('planFeatureValues.starterHistory'),
      pro: t('planFeatureValues.proHistory'),
      business: t('planFeatureValues.businessHistory'),
    },
    { labelKey: 'priorityQueue', starter: false, pro: true, business: true },
    {
      labelKey: 'teamSeats',
      starter: t('planFeatureValues.starterSeats'),
      pro: t('planFeatureValues.proSeats'),
      business: t('planFeatureValues.businessSeats'),
    },
    { labelKey: 'support', starter: false, pro: false, business: true },
  ];

  const plans: Array<{
    id: 'starter' | 'pro' | 'business';
    name: string;
    price: string;
    period: string;
    highlighted: boolean;
    badge?: string;
  }> = [
    {
      id: 'starter',
      name: t('plans.starter.name'),
      price: t('plans.starter.price'),
      period: t('planUnit'),
      highlighted: false,
    },
    {
      id: 'pro',
      name: t('plans.pro.name'),
      price: t('plans.pro.price'),
      period: t('planUnit'),
      highlighted: true,
      badge: t('planRecommended'),
    },
    {
      id: 'business',
      name: t('plans.business.name'),
      price: t('plans.business.price'),
      period: t('planUnit'),
      highlighted: false,
    },
  ];

  return (
    <div className="landing-page min-h-dvh bg-k-canvas text-k-ink">
      {/* ── Sticky Nav ── */}
      <nav
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-all duration-300',
          scrolled
            ? 'border-b-2 border-brand-indigo bg-k-canvas/95 backdrop-blur-md'
            : 'bg-transparent',
        )}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo className="h-9 w-9 text-brand-indigo" />
            <span className="font-display text-lg font-bold tracking-tight text-brand-indigo">
              AgentCAD
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            <Link
              to="/login"
              className="hidden rounded-lg px-4 py-2 text-sm font-bold text-brand-indigo transition-colors hover:bg-brand-indigo/10 sm:block"
            >
              {t('navLogin')}
            </Link>
            <Link
              to="/login"
              className="group inline-flex items-center gap-2 rounded-xl bg-brand-indigo px-3 py-2.5 text-sm font-bold text-brand-cream transition-colors hover:bg-brand-indigo-hover sm:px-5"
            >
              <span className="hidden sm:inline">{t('navStartFree')}</span>
              <span className="sm:hidden">{t('navLogin')}</span>
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden pb-16 pt-32 sm:pb-24 sm:pt-40">
        {/* Flat color band — upper left teal block */}
        <div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-brand-teal-soft/40" />
        <div className="pointer-events-none absolute -right-32 top-32 h-96 w-96 rounded-full bg-brand-teal/30" />

        {/* 3D wireframe */}
        <div className="pointer-events-none absolute inset-0">
          <HeroScene />
        </div>

        <div className="relative z-10 mx-auto max-w-5xl px-4 text-center sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mb-6 inline-block rounded-full border-2 border-brand-indigo bg-brand-cream px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-indigo"
          >
            AI-powered parametric CAD
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-[2.75rem] font-extrabold leading-[0.98] tracking-tight text-brand-indigo sm:text-7xl lg:text-[5.5rem]"
          >
            {t('heroTitle1')}
            <span className="relative ml-2 inline-block">
              <span className="relative z-10">{t('heroTitle2')}</span>
              <span
                aria-hidden
                className="absolute inset-x-0 bottom-1 -z-0 h-3 bg-brand-teal sm:h-5 lg:h-7"
              />
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-brand-indigo/80 sm:text-lg"
          >
            {t('heroSubtitle')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
          >
            <Link
              to="/login"
              className="group inline-flex items-center gap-2 rounded-xl bg-brand-indigo px-8 py-4 text-sm font-bold text-brand-cream transition-colors hover:bg-brand-indigo-hover sm:text-base"
            >
              {t('heroCta')}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <span className="text-sm font-medium text-brand-indigo/70">
              {t('heroCtaNote')}
            </span>
          </motion.div>
        </div>
      </section>

      {/* ── Stats — indigo banner ── */}
      <section className="bg-brand-indigo">
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-10 px-4 py-16 sm:grid-cols-3 sm:gap-6 sm:px-6 sm:py-20">
          <AnimatedStat
            value={90}
            suffix={t('statTimeSuffix')}
            label={t('statTimeSaved')}
          />
          <AnimatedStat
            value={30}
            suffix={t('statSetupSuffix')}
            label={t('statSetup')}
          />
          <AnimatedStat
            value={0}
            suffix={t('statCostSuffix')}
            label={t('statCost')}
          />
        </div>
      </section>

      {/* ── Features — Bento Grid ── */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <div className="mb-14">
              <span className="inline-block rounded-full bg-brand-teal px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-indigo">
                {t('featuresEyebrow')}
              </span>
              <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-brand-indigo sm:text-5xl">
                {t('featuresHeadline')}
              </h2>
            </div>
          </Reveal>

          <div className="grid gap-4 lg:grid-cols-3">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.titleKey} delay={i * 0.08} className={f.span}>
                  <div className="group h-full rounded-2xl border-2 border-brand-indigo/15 bg-k-card p-7 transition-all duration-200 hover:border-brand-indigo">
                    <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal text-brand-indigo">
                      <Icon className="h-6 w-6" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display text-xl font-bold text-brand-indigo">
                      {t(f.titleKey)}
                    </h3>
                    <p className="mt-2 text-sm font-bold uppercase tracking-wider text-brand-indigo/70">
                      {t(f.catchKey)}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-k-ink/80">
                      {t(f.descKey)}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── How It Works — light teal section ── */}
      <section className="border-y-2 border-brand-indigo bg-brand-teal-soft/40 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <div className="mb-16 text-center">
              <span className="inline-block rounded-full bg-brand-indigo px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-cream">
                {t('stepsEyebrow')}
              </span>
              <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-brand-indigo sm:text-5xl">
                {t('stepsHeadline')}
              </h2>
            </div>
          </Reveal>

          <div className="relative grid gap-8 sm:grid-cols-3 sm:gap-6">
            {steps.map((step, i) => (
              <Reveal key={step.number} delay={i * 0.12}>
                <div className="relative flex flex-col items-center text-center">
                  <div className="relative z-10 mb-6">
                    <div className="flex h-20 w-20 items-center justify-center rounded-2xl border-2 border-brand-indigo bg-k-card">
                      <span className="font-display text-3xl font-extrabold text-brand-indigo">
                        {step.number}
                      </span>
                    </div>
                  </div>
                  <h3 className="font-display text-lg font-bold text-brand-indigo">
                    {t(step.titleKey)}
                  </h3>
                  <p className="mt-2 max-w-[260px] text-sm leading-relaxed text-k-ink/80">
                    {t(step.descKey)}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases ── */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <div className="mb-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <span className="inline-block rounded-full border-2 border-brand-indigo bg-k-card px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-indigo">
                  {t('useCasesEyebrow')}
                </span>
                <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-brand-indigo sm:text-5xl">
                  {t('useCasesHeadline')}
                </h2>
              </div>
              <p className="max-w-sm text-sm leading-relaxed text-k-ink/80">
                {t('useCasesSub')}
              </p>
            </div>
          </Reveal>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map((uc, i) => {
              const Icon = uc.icon;
              return (
                <Reveal key={uc.titleKey} delay={i * 0.1}>
                  <div className="group h-full rounded-2xl border-2 border-brand-indigo/15 bg-k-card p-7 transition-all duration-200 hover:border-brand-indigo">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal-soft text-brand-indigo">
                      <Icon className="h-6 w-6" strokeWidth={2.5} />
                    </div>
                    <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-indigo/70">
                      {t(uc.audienceKey)}
                    </p>
                    <h3 className="font-display text-xl font-bold text-brand-indigo">
                      {t(uc.titleKey)}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-k-ink/80">
                      {t(uc.descKey)}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Pricing — indigo section ── */}
      <section className="border-y-2 border-brand-indigo bg-brand-indigo py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <div className="mb-14 text-center">
              <span className="inline-block rounded-full bg-brand-teal px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-indigo">
                {t('pricingEyebrow')}
              </span>
              <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-brand-cream sm:text-5xl">
                {t('pricingHeadline')}
              </h2>
              <p className="mx-auto mt-4 max-w-md text-sm text-brand-cream/80">
                {t('pricingSub')}
              </p>
            </div>
          </Reveal>

          <div className="grid gap-5 lg:grid-cols-3">
            {plans.map((plan, i) => (
              <Reveal key={plan.id} delay={i * 0.1}>
                <div
                  className={cn(
                    'relative flex h-full flex-col rounded-2xl border-2 p-7 transition-colors duration-200',
                    plan.highlighted
                      ? 'border-brand-teal bg-brand-cream'
                      : 'border-brand-cream/30 bg-brand-indigo-hover',
                  )}
                >
                  {plan.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-teal px-4 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-indigo">
                      {plan.badge}
                    </span>
                  )}
                  <h3
                    className={cn(
                      'font-display text-xl font-bold',
                      plan.highlighted ? 'text-brand-indigo' : 'text-brand-cream',
                    )}
                  >
                    {plan.name}
                  </h3>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span
                      className={cn(
                        'font-display text-5xl font-extrabold tracking-tight',
                        plan.highlighted ? 'text-brand-indigo' : 'text-brand-cream',
                      )}
                    >
                      {plan.price}
                    </span>
                    <span
                      className={cn(
                        'text-sm font-medium',
                        plan.highlighted ? 'text-brand-indigo/70' : 'text-brand-cream/70',
                      )}
                    >
                      {plan.period}
                    </span>
                  </div>

                  <div
                    className={cn(
                      'my-6 h-px',
                      plan.highlighted ? 'bg-brand-indigo/15' : 'bg-brand-cream/15',
                    )}
                  />

                  <ul className="flex-1 space-y-3.5">
                    {planFeatures.map((pf) => {
                      const value =
                        plan.id === 'starter'
                          ? pf.starter
                          : plan.id === 'pro'
                            ? pf.pro
                            : pf.business;
                      const labelClass = plan.highlighted
                        ? 'text-brand-indigo/80'
                        : 'text-brand-cream/80';
                      const valueClass = plan.highlighted
                        ? 'text-brand-indigo font-bold'
                        : 'text-brand-cream font-bold';
                      return (
                        <li
                          key={pf.labelKey}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className={labelClass}>
                            {t('planFeatureLabels.' + pf.labelKey)}
                          </span>
                          <span className={valueClass}>
                            {typeof value === 'boolean' ? (
                              value ? (
                                <Check className="h-4 w-4" strokeWidth={3} />
                              ) : (
                                <Minus
                                  className={cn(
                                    'h-4 w-4',
                                    plan.highlighted
                                      ? 'text-brand-indigo/30'
                                      : 'text-brand-cream/30',
                                  )}
                                />
                              )
                            ) : (
                              value
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  <Link
                    to="/login"
                    className={cn(
                      'mt-8 inline-flex items-center justify-center rounded-xl py-3 text-sm font-bold transition-colors',
                      plan.highlighted
                        ? 'bg-brand-indigo text-brand-cream hover:bg-brand-indigo-hover'
                        : 'border-2 border-brand-cream text-brand-cream hover:bg-brand-cream hover:text-brand-indigo',
                    )}
                  >
                    {t('planTryFree')}
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="relative overflow-hidden py-24 sm:py-32">
        <div className="pointer-events-none absolute -left-32 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-brand-teal/30" />
        <div className="pointer-events-none absolute -right-32 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-brand-teal-soft/40" />

        <Reveal>
          <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-brand-indigo sm:text-5xl lg:text-6xl">
              {t('finalCtaTitle1')}
              <span className="relative ml-2 inline-block">
                <span className="relative z-10">{t('finalCtaTitle2')}</span>
                <span
                  aria-hidden
                  className="absolute inset-x-0 bottom-1 -z-0 h-3 bg-brand-teal sm:h-4 lg:h-6"
                />
              </span>
            </h2>
            <p className="mx-auto mt-6 max-w-lg text-base text-brand-indigo/80 sm:text-lg">
              {t('finalCtaSub')}
            </p>
            <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Link
                to="/login"
                className="group inline-flex items-center gap-2 rounded-xl bg-brand-indigo px-8 py-4 text-sm font-bold text-brand-cream transition-colors hover:bg-brand-indigo-hover sm:text-base"
              >
                {t('finalCtaButton')}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <span className="text-sm font-medium text-brand-indigo/70">
                {t('finalCtaNote')}
              </span>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t-2 border-brand-indigo bg-brand-indigo py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:flex-row sm:px-6">
          <div className="flex items-center gap-2.5">
            <Logo className="h-7 w-7 text-brand-cream" />
            <span className="text-xs font-medium text-brand-cream/70">
              {t('footerCopyright')}
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/terms"
              className="text-xs font-medium text-brand-cream/70 transition-colors hover:text-brand-cream"
            >
              {t('footerTerms')}
            </Link>
            <Link
              to="/privacy"
              className="text-xs font-medium text-brand-cream/70 transition-colors hover:text-brand-cream"
            >
              {t('footerPrivacy')}
            </Link>
            <Link
              to="/contact"
              className="text-xs font-medium text-brand-cream/70 transition-colors hover:text-brand-cream"
            >
              {t('footerContact')}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
